import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { MiDatePicker, MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCalendarStartOutline } from "@mdi/js";
import { useTheme } from "@mui/material";

const rescheduleSchema = z.object({
  contactName: z.string().min(1, "Contact Name is required."),
  rescheduleReasonId: z
    .string()
    .uuid()
    .min(1, "Reschedule Reason is required."),
  rescheduleDate: z.string().refine((value) => value !== "Invalid date", {
    message: "The value cannot be 'Invalid date'",
  }),
  confirmAfterReschedule: z.boolean().optional(),
});

const initialRescheduleValues = {
  contactName: "",
  rescheduleReasonId: "",
  rescheduleDate: formatDate(
    new Date(),
    DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
  ),
  confirmAfterReschedule: false,
};

export const RescheduleOrderModal = ({
  open,
  onClose,
  onReschedule,
  orderReasons,
  isLoading,
}) => {
  const theme = useTheme();
  const {
    setValue,
    control,
    reset,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: initialRescheduleValues,
    resolver: zodResolver(rescheduleSchema),
  });

  const rescheduleDate = useWatch({
    control,
    name: "rescheduleDate",
    defaultValue: initialRescheduleValues.rescheduleDate,
  });

  const handleDateChange = (date) => {
    setValue(
      "rescheduleDate",
      formatDate(date, DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const rescheduleModalFields = useMemo(
    () => [
      {
        label: "Contact Name",
        name: "contactName",
        required: true,
        width: "100%",
        formControl: control,
      },
      {
        label: "Reschedule Reason",
        name: "rescheduleReasonId",
        options: orderReasons?.rescheduleReasons,
        fieldType: INPUT_TYPES.SELECT,
        required: true,
        formControl: control,
      },
      {
        label: "Reschedule Date",
        name: "rescheduleDate",
        required: true,
        fieldType: INPUT_TYPES.CUSTOM,
        formControl: control,
        width: "100%",
        customComponent: (
          <MiDatePicker
            value={rescheduleDate}
            onChange={handleDateChange}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            actions={[DATEPICKER_ACTIONS.CANCEL]}
          />
        ),
      },
      {
        label: "Confirm Order After Reschedule",
        name: "confirmAfterReschedule",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        handleChange: ({ target: { checked } }) => {
          setValue("confirmAfterReschedule", checked, {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
        width: "100%",
        style: { padding: theme.spacing(0.5, 0) },
      },
    ],
    [orderReasons, control, handleDateChange]
  );

  const handleFormSubmit = async (data) => {
    await onReschedule(data);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={330}
      titleIcon={mdiCalendarStartOutline}
      leftIconColor={theme.palette.primary.main}
      title="Change Requested Date"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Reschedule",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: !isValid || !isDirty || isLoading,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <MiDetailFields detailFields={rescheduleModalFields} />
    </MiModal>
  );
};
